import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/utils/api';
import {
  Compass, Tag, QrCode, Bookmark, User,
  LayoutDashboard, Percent, UtensilsCrossed, MessageSquare, Store
} from 'lucide-react';

const dinerTabs = [
  { name: 'Discover', icon: Compass, path: '/diner/discover' },
  { name: 'Offers', icon: Tag, path: '/diner/offers' },
  { name: 'Scan', icon: QrCode, path: '/diner/scan' },
  { name: 'Saved', icon: Bookmark, path: '/diner/saved' },
  { name: 'Profile', icon: User, path: '/diner/profile' },
];

const vendorTabs = [
  { name: 'Home',     icon: LayoutDashboard, path: '/vendor/dashboard' },
  { name: 'Offers',   icon: Percent,         path: '/vendor/offers' },
  { name: 'Menu',     icon: UtensilsCrossed, path: '/vendor/menu' },
  { name: 'Feedback', icon: MessageSquare,   path: '/vendor/feedback' },
  { name: 'Profile',  icon: Store,           path: '/vendor/profile' },
];

export default function BottomNav() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [savedBadge, setSavedBadge] = useState(
    () => parseInt(sessionStorage.getItem('da_saved_badge') || '0', 10)
  );
  const [feedbackBadge, setFeedbackBadge] = useState(0);

  // Saved-offers badge (diner)
  useEffect(() => {
    const handler = (e) => {
      const count = e.detail?.count || 0;
      setSavedBadge(count);
      sessionStorage.setItem('da_saved_badge', String(count));
    };
    window.addEventListener('da_saved_badge', handler);
    return () => window.removeEventListener('da_saved_badge', handler);
  }, []);

  // Unresolved-feedback badge (vendor) — polls every 60 s
  useEffect(() => {
    if (user?.role !== 'vendor') return;
    const fetch = () => {
      api.get('/vendor/feedback', { status: 'unresolved', limit: 1 })
        .then(r => setFeedbackBadge(r.data?.total || 0))
        .catch(() => {});
    };
    fetch();
    const timer = setInterval(fetch, 60000);
    return () => clearInterval(timer);
  }, [user?.role]);

  if (!user || user.role === 'admin') return null;
  if (!location.pathname.startsWith('/diner') && !location.pathname.startsWith('/vendor')) return null;

  const tabs = user.role === 'vendor' ? vendorTabs : dinerTabs;
  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-purple-100 md:hidden"
      style={{ boxShadow: '0 -4px 20px rgba(91,33,182,0.08)' }}>
      <div className="flex items-center justify-around px-1 py-1.5">
        {tabs.map(({ name, icon: Icon, path }) => {
          const active = isActive(path);
          const isSaved = name === 'Saved' && user.role === 'diner';
          const isFeedback = name === 'Feedback' && user.role === 'vendor';
          return (
            <button key={path} onClick={() => navigate(path)}
              className="flex flex-col items-center gap-0.5 px-2 py-1 rounded-xl transition-all min-w-0 flex-1">
              <div className={`relative p-1.5 rounded-xl transition-all ${active ? 'bg-[#EDE9FE]' : ''}`}>
                <Icon className={`w-5 h-5 ${active ? 'text-[#5B21B6]' : 'text-gray-400'}`} strokeWidth={active ? 2.2 : 1.8} />
                {isSaved && savedBadge > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[15px] h-[15px] px-0.5 bg-red-500 rounded-full flex items-center justify-center text-[8px] font-extrabold text-white">
                    {savedBadge > 9 ? '9+' : savedBadge}
                  </span>
                )}
                {isFeedback && feedbackBadge > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[15px] h-[15px] px-0.5 bg-amber-500 rounded-full flex items-center justify-center text-[8px] font-extrabold text-white">
                    {feedbackBadge > 9 ? '9+' : feedbackBadge}
                  </span>
                )}
              </div>
              <span className={`text-[10px] font-semibold leading-none ${active ? 'text-[#5B21B6]' : 'text-gray-400'}`}>
                {name}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
